/**
 * Custom hook for authenticated API calls in embedded routes
 *
 * Embedded routes use cookie-based authentication (no Clerk tokens).
 * Session is created via /api/v1/embedded/auth when iframe loads.
 * API calls use cookies + X-Selected-Org-ID header.
 */

import { useEmbeddedOrg } from '@/contexts/EmbeddedOrgContext';
import { fetchJSON } from '@/lib/fetch-utils';
import { layerHeaders, type AuthenticatedFetchOptions } from '@/lib/request-headers';
import { API_BASE_URL } from '@/lib/api/config';
import { embeddedSessionAuthHeaders } from '@/lib/embedded-host';
import { notifyEmbeddedSessionExpired } from '@/lib/embedded-session-recovery';
import {
  accessRevisionRequestHeaders,
  beginAccessFence,
  fetchRawWithAccessFence,
} from '@/lib/access-revision';
import { useCallback } from 'react';

export interface AuthData {
  selectedOrgId: string;
}

/**
 * Hook for authenticated API calls in embedded routes
 *
 * Uses cookie-based auth (no Clerk tokens needed).
 * Returns:
 * - getAuthData: Get orgId (throws if missing)
 * - authenticatedFetch: Wrapper with automatic X-Selected-Org-ID header
 */
const CSRF_PROTECTED_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Rewrite a standard `/api/v1/*` path to its `/api/v1/embedded/*` equivalent and
 * attach the `_org` routing hint. Authentication is the cookie or the
 * `Authorization` header; no bearer/session identifier is ever placed in a
 * request URL.
 */
function buildEmbeddedUrl(url: string, orgId: string): string {
  let finalUrl = url;
  if (url.includes('/api/v1/') && !url.includes('/api/v1/embedded/')) {
    finalUrl = url.replace('/api/v1/', '/api/v1/embedded/');
  }

  const separator = finalUrl.includes('?') ? '&' : '?';
  finalUrl = `${finalUrl}${separator}_org=${encodeURIComponent(orgId)}`;

  // Prepend API base URL for cross-origin requests to edge-api.
  return finalUrl.startsWith('http') ? finalUrl : `${API_BASE_URL}${finalUrl}`;
}

export function useEmbeddedAuthFetch() {
  const { orgId, csrfToken } = useEmbeddedOrg();

  /**
   * Get authentication data (orgId only, session via cookies)
   * Throws if orgId is missing
   */
  const getAuthData = useCallback((): Promise<AuthData> => {
    if (!orgId) {
      return Promise.reject(new Error('Organization not loaded'));
    }

    return Promise.resolve({ selectedOrgId: orgId });
  }, [orgId]);

  /**
   * Authenticated fetch wrapper - adds X-Selected-Org-ID header
   * Uses cookies for authentication (credentials: 'include')
   * Automatically rewrites /api/v1/* endpoints to /api/v1/embedded/*
   */
  const fetchWithAuth = useCallback(
    async <T = unknown>(
      url: string,
      options?: AuthenticatedFetchOptions,
      maxRetries = 3
    ): Promise<T> => {
      const { selectedOrgId } = await getAuthData();
      const { extraHeaders, accessRead, expectedRefusals, ...init } = options ?? {};

      const fullUrl = buildEmbeddedUrl(url, selectedOrgId);

      // The bearer of a Uspacy frame rides here; Bitrix24's session is the
      // cookie and this adds nothing (spec 2026-10-29-uspacy-card-tab-session.md §10.2).
      const own: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Selected-Org-ID': selectedOrgId,
        ...embeddedSessionAuthHeaders(),
      };

      // The embedded session middleware enforces CSRF on state-changing methods
      // (POST/PUT/PATCH/DELETE) and rejects a missing token with 403. The token
      // is issued with the embedded session, so attach it to writes.
      const method = (init.method ?? 'GET').toUpperCase();
      if (CSRF_PROTECTED_METHODS.has(method) && csrfToken) {
        own['X-CSRF-Token'] = csrfToken;
      }
      // Caller extras under the session headers (same rule as the standalone
      // wrapper): `Idempotency-Key` gets through, the org and CSRF headers
      // cannot be overridden in any casing.
      // The known access revision rides along (E1.5 PR 4b) and the answer is
      // judged against it — the same fence as the portal wrapper's, opened
      // after the last await before the request.
      const fence = beginAccessFence(selectedOrgId, {
        method,
        url: fullUrl,
        read: accessRead,
      });
      const headers = layerHeaders(extraHeaders, {
        ...own,
        ...accessRevisionRequestHeaders(fence),
      });

      return fetchJSON<T>(
        fullUrl,
        {
          ...init,
          headers,
          credentials: 'include', // Include cookies for session auth
        },
        maxRetries,
        fence,
        expectedRefusals
      );
    },
    [getAuthData, csrfToken]
  );

  /**
   * Authenticated fetch for BINARY responses (e.g. record audio) — returns the
   * raw Response so the caller can read it as a Blob. Mirrors fetchWithAuth's
   * URL rewrite + cookie auth, but skips JSON parsing and the JSON Content-Type.
   */
  const fetchBlob = useCallback(
    async (path: string): Promise<Response> => {
      const { selectedOrgId } = await getAuthData();
      const fullUrl = buildEmbeddedUrl(path, selectedOrgId);
      // A raw Response, so the fence of the JSON wrapper is applied here: the
      // known revision rides along and a late old-revision answer throws
      // before anyone reads the audio (E1.5 PR 4b, Codex P1 on #1745).
      const response = await fetchRawWithAccessFence(selectedOrgId, fullUrl, {
        headers: { 'X-Selected-Org-ID': selectedOrgId, ...embeddedSessionAuthHeaders() },
        credentials: 'include', // the cookie session; a bearer one adds its header above
      });
      // The raw path skips `fetchJSON`, which is where every other embedded
      // 401 starts the session recovery: audio can be the first request after
      // the session lapsed, and the frame must not stay open on a dead one.
      if (response.status === 401) notifyEmbeddedSessionExpired();
      return response;
    },
    [getAuthData]
  );

  return {
    getAuthData,
    authenticatedFetch: fetchWithAuth,
    fetchBlob,
  };
}
